import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, Dropdown, Menu } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PurchaseOrderService } from '../../../Services/Manage/PurchaseOrderService';
import { ExpenseServices } from '../../../Services/Manage/ExpenseServices';
import PoPayments from '../../modals/PoPayments';
import {
    EditOutlined, EyeOutlined, ShoppingCartOutlined, ClockCircleOutlined,
    SyncOutlined, CheckCircleOutlined, CloseCircleOutlined,
    DownloadOutlined, PlusOutlined, FilterOutlined, CalendarOutlined, DownOutlined,
} from '@ant-design/icons';
import { Modal, Typography as AntTypography } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const PERMISSION_LINK = '/inventory/purchase-orders';

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const STATUS_COLORS = {
    "Pending": "#fa8c16",
    "Partial Received": "#faad14",
    "Fully Received": "#52c41a",
    "Cancelled": "#f5222d",
};

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const PurchaseOrdersDashboard = () => {
    const perm = getPermission(PERMISSION_LINK);
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allPOs, setAllPOs] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentsByPo, setPaymentsByPo] = useState({});
const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [selectedPO, setSelectedPO] = useState(null);
    const [dateFilterRange, setDateFilterRange] = useState(null); // null = All Time (default)
    const [dateFilterLabel, setDateFilterLabel] = useState('All Time');

    const getAllPOs = async () => {
        setLoading(true);
        try {
            const request = await PurchaseOrderService.getAllPO();
            const data = request.data || [];
            setAllPOs(data);
            fetchAllPayments(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const deriveStatus = (paid, total, currentStatus) => {
        if (currentStatus === "Cancelled") return "Cancelled";
        if (total > 0 && paid >= total) return "Fully Received";
        if (paid > 0) return "Partial Received";
        return "Pending";
    };

    const fetchAllPayments = async (pos) => {
        try {
            const results = await Promise.all(
                pos.map((po) => ExpenseServices.getPaymentByPoId(po.po_number).catch(() => ({ data: [] })))
            );
            const map = {};
            const statusUpdates = [];

            pos.forEach((po, idx) => {
                const paid = (results[idx]?.data || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);
                map[po.po_number] = paid;

                const newStatus = deriveStatus(paid, Number(po.total_amount || 0), po.status);
                if (newStatus !== po.status) {
                    statusUpdates.push({ id: po.id, status: newStatus });
                }
            });

            setPaymentsByPo(map);

            if (statusUpdates.length) {
                await Promise.all(
                    statusUpdates.map((u) => PurchaseOrderService.updatePO({ status: u.status }, u.id).catch(() => null))
                );
                setAllPOs((prev) =>
                    prev.map((po) => {
                        const match = statusUpdates.find((u) => u.id === po.id);
                        return match ? { ...po, status: match.status } : po;
                    })
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handlePayNow = (record) => {
        setSelectedPO(record);
        setPaymentModalOpen(true);
    };

    const onPaymentSuccess = () => {
        setPaymentModalOpen(false);
        setSelectedPO(null);
        getAllPOs();
    };

    useEffect(() => {
        getAllPOs();
    }, []);

    const visiblePOs = useMemo(
        () => perm.view_all ? allPOs : allPOs.filter((s) => String(s.added_by) === String(currentUserId)),
        [allPOs, perm.view_all, currentUserId]
    );

    // Date-filtered base for stat cards/charts — reacts to the "More Actions"
    // date preset only, not search/status/supplier.
    const dateFilteredPOs = useMemo(() => {
        if (!dateFilterRange) return visiblePOs;
        return visiblePOs.filter((s) => {
            const d = dayjs(s.created_at);
            return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
        });
    }, [visiblePOs, dateFilterRange]);

    const filteredPOs = useMemo(() => {
        let filtered = visiblePOs;

        if (searchText) {
            filtered = filtered.filter((s) =>
                Object.values(s).some((value) =>
                    value?.toString().toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }
        if (selectedSupplier) filtered = filtered.filter((s) => s.supplier_name === selectedSupplier);
        if (selectedStatus) filtered = filtered.filter((s) => s.status === selectedStatus);
        if (dateFilterRange) {
            filtered = filtered.filter((s) => {
                const d = dayjs(s.created_at);
                return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
            });
        }

        return filtered;
    }, [visiblePOs, searchText, selectedStatus, selectedSupplier, dateFilterRange]);

const totalPOs = visiblePOs.length; // always all-time
    const pendingCount = useMemo(() => dateFilteredPOs.filter((s) => s.status === "Pending").length, [dateFilteredPOs]);
    const partialCount = useMemo(() => dateFilteredPOs.filter((s) => s.status === "Partial Received").length, [dateFilteredPOs]);
    const fullCount = useMemo(() => dateFilteredPOs.filter((s) => s.status === "Fully Received").length, [dateFilteredPOs]);
    const cancelledCount = useMemo(() => dateFilteredPOs.filter((s) => s.status === "Cancelled").length, [dateFilteredPOs]);

    const statusSummaryData = useMemo(() => {
        const counts = { "Pending": pendingCount, "Partial Received": partialCount, "Fully Received": fullCount, "Cancelled": cancelledCount };
        return Object.entries(counts)
            .filter(([, v]) => v > 0)
            .map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] }));
    }, [pendingCount, partialCount, fullCount, cancelledCount]);
    const statusSummaryTotal = statusSummaryData.reduce((a, b) => a + b.value, 0);

const topSuppliersData = useMemo(() => {
        const map = {};
        dateFilteredPOs.forEach((s) => {
            const key = s.supplier_name || "Unknown";
            map[key] = (map[key] || 0) + Number(s.total_amount || 0);
        });
        return Object.entries(map)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);
    }, [dateFilteredPOs]);

    const supplierOptions = [...new Set(visiblePOs.map((s) => s.supplier_name).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const statusOptions = ["Pending", "Partial Received", "Fully Received", "Cancelled"].map((s) => ({ label: s, value: s }));

    const REPORT_COLUMNS = [
        { label: "PO Number" },
        { label: "Supplier" },
        { label: "Order Date" },
        { label: "Delivery Date" },
        { label: "Total Amount (Rs.)" },
        { label: "Paid Amount (Rs.)" },
        { label: "Pending Amount (Rs.)" },
        { label: "Status" },
    ];

    const buildReportRows = () => filteredPOs.map((r) => {
        const paid = paymentsByPo[r.po_number] || 0;
        const pending = Number(r.total_amount || 0) - paid;
        return {
            "PO Number": r.po_number || "-",
            "Supplier": r.supplier_name || "-",
            "Order Date": r.po_date ? new Date(r.po_date).toLocaleDateString('en-GB') : '-',
            "Delivery Date": r.delivery_date ? new Date(r.delivery_date).toLocaleDateString('en-GB') : '-',
            "Total Amount (Rs.)": Number(r.total_amount || 0),
            "Paid Amount (Rs.)": paid,
            "Pending Amount (Rs.)": pending > 0 ? pending : 0,
            "Status": r.status || "-",
        };
    });

    const dateStamp = () => dayjs().format("YYYY-MM-DD");

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Purchase Orders Report");
        XLSX.writeFile(wb, `Purchase-Orders-Report-${dateStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Purchase Orders Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Purchase-Orders-Report-${dateStamp()}.pdf`);
    };

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        { title: 'PO Number', dataIndex: 'po_number', key: 'po_number' },
        { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', render: (v) => v || '-' },
        { title: 'Order Date', dataIndex: 'po_date', key: 'po_date', render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-' },
        { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-' },
        {
            title: 'Total Amount (₹)',
            dataIndex: 'total_amount',
            key: 'total_amount',
            align: 'right',
            render: (v) => Number(v || 0).toLocaleString('en-IN'),
        },
        {
            title: 'Paid Amount (₹)',
            key: 'paid_amount',
            align: 'right',
            render: (_, record) => {
                const paid = paymentsByPo[record.po_number] || 0;
                return <span style={{ color: '#52c41a' }}>{paid.toLocaleString('en-IN')}</span>;
            },
        },
        {
            title: 'Pending Amount (₹)',
            key: 'pending_amount',
            align: 'right',
            render: (_, record) => {
                const paid = paymentsByPo[record.po_number] || 0;
                const pending = Number(record.total_amount || 0) - paid;
                return pending > 0
                    ? <span style={{ color: '#fa541c' }}>{pending.toLocaleString('en-IN')}</span>
                    : <span>0</span>;
            },
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <span style={{
                    background: `${STATUS_COLORS[record.status] || '#8c8c8c'}1a`,
                    color: STATUS_COLORS[record.status] || '#8c8c8c',
                    padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>
                    {record.status || '-'}
                </span>
            ),
        },
        {
            title: 'Action',
            key: 'Action',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/purchase-orders/view-purchase-order', { state: { id: record.id, action: 'View', enableEdit: false, from: '/inventory/purchase-orders' } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/purchase-orders/edit-purchase-order', { state: { id: record.id, action: 'Edit', enableEdit: true, from: '/inventory/purchase-orders' } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button onClick={() => handlePayNow(record)} type="primary" size="middle" disabled={!perm.edit}>
                            Pay Now
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Modal
                width={800}
                title={<AntTypography.Title level={4}>Add Payment for: {selectedPO?.po_number}</AntTypography.Title>}
                open={paymentModalOpen}
                onCancel={() => setPaymentModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                {selectedPO && (
                    <PoPayments
                        getAllExpense={onPaymentSuccess}
                        quotationNo={selectedPO?.po_number}
                        grandTotal={Number(selectedPO?.total_amount || 0)}
                    />
                )}
            </Modal>
            <Card bordered={false} bodyStyle={{ padding: 0 }}>
                <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                        <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Purchase Orders</Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Purchase Orders</Typography.Text>
                    </div>
           <Space wrap>
                        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                            <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                        </Dropdown>
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <Menu
                                    onClick={({ key }) => {
                                        let range = null;
                                        let label = 'All Time';
                                        if (key === 'today') {
                                            range = [dayjs().startOf('day'), dayjs().endOf('day')];
                                            label = 'Today';
                                        } else if (key === 'last7') {
                                            range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                                            label = 'Last 7 Days';
                                        } else if (key === 'last30') {
                                            range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                                            label = 'Last 30 Days';
                                        } else if (key === 'lastMonth') {
                                            range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                                            label = 'Last Month';
                                        } else if (key === 'thisYear') {
                                            range = [dayjs().startOf('year'), dayjs()];
                                            label = 'This Year';
                                        } else if (key === 'all') {
                                            range = null;
                                            label = 'All Time';
                                        }
                                        setDateFilterRange(range);
                                        setDateFilterLabel(label);
                                    }}
                                    items={[
                                        { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                                        { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                                        { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                                        { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                                        { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                                        { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                                    ]}
                                />
                            }
                        >
                            <Button size="middle" icon={<DownOutlined />}>
                                More Actions ({dateFilterLabel})
                            </Button>
                        </Dropdown>
                        <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                            <Button
                                size="middle" type="primary" disabled={!perm.add}
                                icon={<PlusOutlined />}
                                onClick={() => navigate('/inventory/purchase-orders/add-purchase-order', { state: { action: 'Add', enableEdit: true, from: '/inventory/purchase-orders' } })}
                                style={{ background: '#1677ff', borderColor: '#1677ff' }}
                            >
                                New Purchase Order
                            </Button>
                        </Tooltip>
                    </Space>
                </div>

                <div style={{ padding: '8px 16px 0' }}>
                    <Row gutter={[8, 8]}>
                        <Col xs={24} sm={12} md={5}>
                            <StatCard title="Total POs" value={totalPOs} sub="View all orders" color="#722ed1" bg="#f5eeff" icon={<ShoppingCartOutlined />} />
                        </Col>
                        <Col xs={24} sm={12} md={5}>
                            <StatCard title="Pending" value={pendingCount} sub="View pending" color="#fa8c16" bg="#fff7e6" icon={<ClockCircleOutlined />} />
                        </Col>
                        <Col xs={24} sm={12} md={5}>
                            <StatCard title="Partial Received" value={partialCount} sub="View partial " color="#faad14" bg="#fffbe6" icon={<SyncOutlined />} />
                        </Col>
                        <Col xs={24} sm={12} md={5}>
                            <StatCard title="Fully Received" value={fullCount} sub="View completed " color="#52c41a" bg="#f6ffed" icon={<CheckCircleOutlined />} />
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                            <StatCard title="Cancelled" value={cancelledCount} sub="View cancelled " color="#f5222d" bg="#fff1f0" icon={<CloseCircleOutlined />} />
                        </Col>
                    </Row>
                </div>

                <div style={{ padding: '6px 16px 16px' }}>
                    <Row gutter={[8, 8]}>
                        <Col xs={24} lg={17}>
                            <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                                <Row gutter={[16, 16]} align="bottom">
                                    <Col xs={24} sm={12} md={7}>
                                        <Input
                                            allowClear
                                            placeholder="Search PO number, supplier, item..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Select allowClear placeholder="All Status" options={statusOptions}
                                            value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Select allowClear placeholder="All Suppliers" options={supplierOptions}
                                            value={selectedSupplier} onChange={setSelectedSupplier} style={{ width: "100%" }} />
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                    </Col>
                                </Row>
                            </Card>

                            <Table
                                loading={loading}
                                scroll={{ x: 'max-content' }}
                                rowKey="id"
                                dataSource={filteredPOs.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))}
                                columns={columns}
                            />
                        </Col>

                        <Col xs={24} lg={7}>
                            <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                                <Typography.Text strong>PO Status Overview</Typography.Text>
                                {statusSummaryTotal === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                        <span style={{ fontSize: 28 }}>🧾</span>
                                        <span style={{ marginTop: 6 }}>No data yet</span>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                        <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${statusSummaryTotal ? Math.round((v / statusSummaryTotal) * 100) : 0}%)`, n]} />
                                                    <Pie data={statusSummaryData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                        {statusSummaryData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                    </Pie>
                                                </PieChart>
                                            </ResponsiveContainer>
                                            <div style={{
                                                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                                textAlign: 'center', pointerEvents: 'none',
                                            }}>
                                                <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{statusSummaryTotal}</div>
                                                <div style={{ fontSize: 9, color: '#888' }}>Total POs</div>
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {statusSummaryData.map((e, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                        <span style={{ color: '#333' }}>{e.name}</span>
                                                    </span>
                                                    <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({statusSummaryTotal ? Math.round((e.value / statusSummaryTotal) * 100) : 0}%)</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card>

                            <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                                <Typography.Text strong>Top Suppliers (By PO Value)</Typography.Text>
                                {topSuppliersData.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                        <span style={{ fontSize: 28 }}>🏭</span>
                                        <span style={{ marginTop: 6 }}>No data yet</span>
                                    </div>
                                ) : (
                                    <div style={{ marginTop: 12 }}>
                                        {topSuppliersData.map((s, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 10 }}>
                                                <span style={{ color: '#333' }}>{i + 1}. {s.name}</span>
                                                <span style={{ fontWeight: 700, color: '#1658b4' }}>₹ {s.value.toLocaleString('en-IN')}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Card>
        </>
    );
};

export default PurchaseOrdersDashboard;